import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Skull, Loader2, CheckCircle2, XCircle } from "lucide-react";

const CZ_DAYS = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
function fmt(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return `${CZ_DAYS[d.getDay()]} ${dateStr}`;
}

export default function RatePage() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | found | notfound | already | sending | done
  const [info, setInfo] = useState(null);
  const [option, setOption] = useState(null);
  const [text, setText] = useState("");

  useEffect(() => {
    api.get(`/reviews/by-token/${token}`)
      .then(({ data }) => {
        if (!data.found) return setState("notfound");
        setInfo(data);
        setState(data.submitted ? "already" : "found");
      })
      .catch(() => setState("notfound"));
  }, [token]);

  const submit = async () => {
    if (!option) return;
    setState("sending");
    try {
      const { data } = await api.post("/reviews/submit", { token, option_id: option, text });
      setState(data.already ? "already" : "done");
    } catch (e) {
      setState("notfound");
    }
  };

  return (
    <div className="grain-layer min-h-screen bg-void flex items-center justify-center px-5 py-12 relative text-neutral-200">
      <div className="absolute inset-0 scanlines pointer-events-none" />
      <div className="relative z-10 w-full max-w-lg border border-white/10 bg-[#0a0a0a] p-8 border-t-2 border-t-nv" data-testid="rate-page">
        <div className="flex items-center gap-2 justify-center mb-2">
          <Skull className="w-6 h-6 text-red-600" />
          <h1 className="font-head text-xl uppercase tracking-widest text-neutral-100">Přežil jsi HUBERTA?</h1>
        </div>
        <p className="text-center text-xs tracking-[0.3em] text-nv uppercase mb-8">Tvoje reakce · žádné hvězdy</p>

        {state === "loading" && <Loader2 className="w-10 h-10 text-nv animate-spin mx-auto my-8" />}

        {state === "notfound" && (
          <div className="py-6 text-center" data-testid="rate-notfound">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-neutral-400">Odkaz na hodnocení není platný nebo vypršel.</p>
          </div>
        )}

        {state === "already" && (
          <div className="py-6 text-center" data-testid="rate-already">
            <CheckCircle2 className="w-12 h-12 text-nv mx-auto mb-4" />
            <p className="text-neutral-300 font-head">Tvoje hodnocení už máme.</p>
            <p className="text-sm text-neutral-500 mt-2">Děkujeme, že jsi se s námi podělil o svůj zážitek.</p>
          </div>
        )}

        {(state === "found" || state === "sending") && info && (
          <div className="space-y-3">
            {info.date && <p className="text-center text-xs text-neutral-500 mb-4">Termín hry: {fmt(info.date)} · {info.time}</p>}
            {info.options.map((o) => (
              <button
                key={o.id}
                onClick={() => setOption(o.id)}
                data-testid={`rate-option-${o.id}`}
                className={`w-full text-left border p-4 flex items-center gap-4 transition-colors ${option === o.id ? "border-nv bg-nv/10 text-neutral-100" : "border-white/10 bg-[#0b0a09] text-neutral-300 hover:border-nv/40"}`}
              >
                <span className="font-head text-2xl text-[#3a2f2c] w-6">{o.id}</span>
                <span className="font-head text-base sm:text-lg">{o.label}</span>
              </button>
            ))}
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Chceš něco dodat? (nepovinné)"
              data-testid="rate-comment"
              rows={4}
              className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-neutral-200 focus:border-nv outline-none mt-4"
            />
            <button
              onClick={submit}
              disabled={!option || state === "sending"}
              data-testid="rate-submit-btn"
              className="hud-btn vhs-btn w-full font-head tracking-[0.2em] py-4 text-sm disabled:opacity-40"
            >
              {state === "sending" ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "[ ODESLAT HODNOCENÍ ]"}
            </button>
            <p className="text-[11px] text-neutral-600 text-center">Hodnocení projde schválením, než se objeví na webu.</p>
          </div>
        )}

        {state === "done" && (
          <div className="py-6 text-center" data-testid="rate-done">
            <CheckCircle2 className="w-14 h-14 text-nv mx-auto mb-4 glow-nv" />
            <h3 className="font-head text-xl uppercase text-neutral-100 mb-2">Díky!</h3>
            <p className="text-sm text-neutral-400">Tvoje reakce byla odeslána. Ještě se objeví po schválení.</p>
            <a href="/" className="inline-block mt-6 hud-btn vhs-btn px-6 py-3 font-head text-sm">Zpět na web</a>
          </div>
        )}
      </div>
    </div>
  );
}
